import { getRouterInstance } from '@/router/router-instance';

/**
 * "Interaction required" flag — drives whether the login page shows its sign-in
 * button or auto-redirects.
 *
 * Faithful port of legacy `SESSION_STORAGE.INTERACTION_REQUIRED`
 * (`'ey.interaction.needed'`). Legacy sets it in two places and the login
 * component reads it to decide `displayLogin`:
 *   - `session-idle.resetIdle()` error path — token refresh failed on
 *     "Stay signed in",
 *   - `app.component.navigateToLogin()` — a peer tab logged out.
 *
 * When set, the user is shown the EY login page with a button (NOT bounced
 * through the AAD logout screen), so they can re-authenticate with a click.
 */
const INTERACTION_REQUIRED_KEY = 'ey.interaction.needed';

export function markInteractionRequired(): void {
  try {
    sessionStorage.setItem(INTERACTION_REQUIRED_KEY, 'true');
  } catch {
    /* sessionStorage may be unavailable in private mode */
  }
}

export function isInteractionRequired(): boolean {
  try {
    return sessionStorage.getItem(INTERACTION_REQUIRED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearInteractionRequired(): void {
  try {
    sessionStorage.removeItem(INTERACTION_REQUIRED_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Send the user to the EY login page showing the sign-in button — NOT the AAD
 * logout screen. Mirrors legacy `resetIdle` error path / `navigateToLogin`:
 * set the flag, then client-navigate to `/login` WITHOUT a `logoutRedirect`.
 *
 * Used when a token silently expires (e.g. the idle "Stay signed in" refresh
 * fails) so the user re-authenticates with one click instead of being bounced
 * through the Microsoft sign-out flow.
 */
export function goToLoginInteractive(): void {
  markInteractionRequired();
  const router = getRouterInstance();
  if (router) {
    void router.navigate({ to: '/login', replace: true });
  } else {
    // Router not yet registered — fall back to a hard navigation.
    window.location.assign('/login');
  }
}
